
import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, GraduationCap, Home, BookOpen, Heart, Shield, Zap, LayoutGrid, Trophy, MessageCircle, Send, Instagram, ShieldCheck, Copyright, ChevronRight, Box, Hexagon, Cpu, Globe, Tag, MessageSquare, ShieldAlert, ArrowUpRight, Scale } from 'lucide-react';
import { NAV_LINKS, MAIN_COUPON_CODE, TELEGRAM_LINK, WHATSAPP_CHANNEL_LINK, INSTAGRAM_LINK } from '../constants';

interface LayoutProps { children: React.ReactNode; }

const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor" 
    className={className}
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

const Breadcrumbs = () => {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter((x) => x);
  
  if (pathnames.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <ol className="flex items-center space-x-2 text-[10px] font-black uppercase tracking-widest text-gray-500">
        <li>
          <Link to="/" className="hover:text-blue-500 transition-colors flex items-center gap-1">
            <Home size={10} /> HOME
          </Link>
        </li>
        {pathnames.map((value, index) => {
          const last = index === pathnames.length - 1;
          const to = `/${pathnames.slice(0, index + 1).join('/')}`;
          const label = value.replace(/-/g, ' ');

          return (
            <li key={to} className="flex items-center space-x-2">
              <ChevronRight size={10} className="text-gray-800" />
              {last ? (
                <span className="text-blue-500 truncate max-w-[150px]">{label}</span>
              ) : (
                <Link to={to} className="hover:text-white transition-colors uppercase">{label}</Link>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [showHelpMenu, setShowHelpMenu] = useState(false);
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const pageTitles: { [key: string]: string } = {
      '/': 'Official PW Coupon Code 2026 - Max Savings Hub',
      '/blogs': 'Success Hub - PW Aspirant Study Guides & Price Alerts',
      '/edu-offers': 'PW Live Offers & Batch Sale Events 2026',
      '/pw-coupon-code': 'Verified All Batches Discount Code 2026',
      '/exam-categories': 'Browse PW Goal Categories - IIT, NEET, GATE',
    };
    document.title = pageTitles[location.pathname] || 'PW Coupon Hub 2026 - Save on Your Future';
  }, [location.pathname]);

  const getIcon = (label: string) => {
    switch (label.toLowerCase()) {
      case 'home': return <Home size={18} />;
      case 'all batches': return <LayoutGrid size={18} />;
      case 'exam categories': return <LayoutGrid size={18} />;
      case 'jee': return <Trophy size={18} />;
      case 'neet': return <Heart size={18} />;
      case 'gate': return <Shield size={18} />;
      case 'offers': return <Zap size={18} />;
      case 'blog': return <BookOpen size={18} />;
      case 'safety & transparency': return <ShieldCheck size={18} />;
      default: return <Tag size={18} />;
    }
  };

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "EduOffers PW Coupon Hub",
    "url": "https://eduoffers.tech",
    "logo": "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=200&q=80",
    "description": "Premium student portal for verified Physics Wallah coupon codes and batch discounts.",
    "potentialAction": {
      "@type": "UseAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://eduoffers.tech/pw-coupon-code",
        "actionPlatform": ["http://schema.org/DesktopWebPlatform", "http://schema.org/MobileWebPlatform"]
      },
      "name": "Redeem PW Discount"
    }
  };

  return (
    <div className="min-h-screen flex flex-col font-sans bg-[#0a0a0a] text-white relative">
      <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      
      {/* Global 3D World Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute top-[-20%] left-[-10%] w-[70vw] h-[70vw] bg-blue-600/5 blur-[150px] rounded-full animate-pulse-glow"></div>
        <div className="absolute bottom-[-15%] right-[-10%] w-[60vw] h-[60vw] bg-purple-600/5 blur-[150px] rounded-full animate-pulse-glow" style={{animationDelay: '-4s'}}></div>
        
        <div className="absolute top-[10%] left-[5%] animate-float opacity-10 scale-150">
           <Box className="w-16 h-16 text-blue-500 rotate-12" />
        </div>
        <div className="absolute top-[30%] right-[10%] animate-float-delayed opacity-5 scale-125">
           <Hexagon className="w-24 h-24 text-blue-400 rotate-45" />
        </div>
        <div className="absolute bottom-[20%] left-[12%] animate-float-reverse opacity-10 scale-110">
           <Cpu className="w-20 h-20 text-purple-500" />
        </div>
        <div className="absolute top-[50%] left-[40%] animate-orbit opacity-5">
           <Globe className="w-12 h-12 text-blue-300" />
        </div>
        
        <div className="absolute bottom-0 left-0 w-full h-[40vh] bg-[linear-gradient(rgba(37,99,235,0)_0%,rgba(37,99,235,0.05)_100%)] border-t border-white/5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(255,255,255,0.02) 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      {/* FIXED Navigation Bar */}
      <nav className="fixed top-0 left-0 right-0 z-[60] glass-morphism border-b border-white/10 h-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
          <div className="flex justify-between h-full items-center">
            <div className="flex items-center gap-4">
              <button onClick={() => setIsMenuOpen(true)} className="p-2 -ml-2 text-gray-300 hover:text-white hover:bg-white/5 rounded-xl transition-all" aria-label="Open Navigation Menu">
                <Menu size={28} />
              </button>
              <Link to="/" className="flex items-center gap-2 group">
                <div className="bg-blue-600 p-1.5 rounded-lg group-hover:rotate-y-180 transition-transform duration-700 shadow-lg shadow-blue-600/30 perspective-2000 preserve-3d">
                  <GraduationCap className="text-white w-5 h-5" />
                </div>
                <span className="font-black text-lg tracking-tighter text-white uppercase sm:block hidden">EDU<span className="text-blue-500 italic">OFFERS</span></span>
              </Link>
            </div>
            <div className="hidden lg:flex items-center space-x-8">
              {NAV_LINKS.filter(link => link.label !== 'Safety & Transparency').slice(0, 8).map((link) => (
                <Link key={link.path} to={link.path} className={`text-[11px] font-black uppercase tracking-[0.2em] transition-all hover:text-blue-400 relative group ${location.pathname === link.path ? 'text-blue-500' : 'text-gray-400'}`}>
                  {link.label}
                  {location.pathname === link.path && (
                    <span className="absolute -bottom-1 left-0 w-full h-0.5 bg-blue-500 rounded-full"></span>
                  )}
                </Link>
              ))}
            </div>
            <div className="flex items-center">
               <span className="text-[10px] font-black py-1.5 px-4 rounded-full bg-blue-600/10 border border-blue-600/30 text-blue-400 uppercase tracking-widest animate-pulse shadow-[0_0_20px_rgba(37,99,235,0.15)]">Verified 2026: {MAIN_COUPON_CODE}</span>
            </div>
          </div>
        </div>
      </nav>

      {/* CONTENT AREA WITH TOP PADDING FOR FIXED NAV */}
      <div className="pt-16 flex flex-col flex-grow">
        <Breadcrumbs />

        <main className="flex-grow relative z-10">
          {children}
          
          {/* Universal Trust Section - "Lower part of the web" */}
          {location.pathname !== '/safety-and-transparency' && (
            <section className="max-w-7xl mx-auto px-6 mt-20 mb-10">
              <div className="bg-[#111] border border-white/5 rounded-[40px] p-8 md:p-12 relative overflow-hidden group shadow-2xl">
                <div className="absolute top-0 left-0 w-1 bg-blue-600 h-full"></div>
                <div className="flex flex-col md:flex-row items-center justify-between gap-8">
                  <div className="flex items-center gap-6">
                    <div className="bg-blue-600/10 p-5 rounded-[24px] border border-blue-600/20 group-hover:scale-110 transition-transform duration-700">
                      <ShieldCheck className="text-blue-500" size={32} />
                    </div>
                    <div>
                      <h4 className="text-xl font-black text-white uppercase tracking-tight italic">Trust & Transparency Hub</h4>
                      <p className="text-gray-500 text-xs font-bold uppercase tracking-widest mt-1 italic">Verified 2026 Ambassador Security Portal</p>
                    </div>
                  </div>
                  <Link 
                    to="/safety-and-transparency" 
                    className="flex items-center gap-3 bg-white/5 hover:bg-white/10 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.3em] transition-all border border-white/5 group-hover:gap-6"
                  >
                    Enter Security Center <ArrowUpRight size={14} />
                  </Link>
                </div>
              </div>
            </section>
          )}
        </main>

        {/* Enhanced Floating Support Hub */}
        <div className="fixed bottom-8 right-8 z-[100] flex flex-col items-end gap-4">
          {showHelpMenu && (
            <div className="flex flex-col gap-3 mb-2 animate-in slide-in-from-bottom-4 fade-in duration-300">
              <a 
                href={WHATSAPP_CHANNEL_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#25D366] hover:bg-[#128C7E] text-white px-6 py-4 rounded-[20px] shadow-2xl transition-all hover:scale-105 active:scale-95 group"
              >
                <WhatsAppIcon size={20} className="group-hover:rotate-12 transition-transform" />
                <span className="font-black text-xs uppercase tracking-widest">WhatsApp Channel</span>
              </a>
              <a 
                href={TELEGRAM_LINK} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-blue-500 hover:bg-blue-600 text-white px-6 py-4 rounded-[20px] shadow-2xl transition-all hover:scale-105 active:scale-95 group"
              >
                <Send size={20} className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                <span className="font-black text-xs uppercase tracking-widest">Telegram Hub</span>
              </a>
            </div>
          )}
          <button 
            onClick={() => setShowHelpMenu(!showHelpMenu)}
            className={`group flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-7 py-4.5 rounded-[24px] shadow-[0_20px_40px_rgba(37,99,235,0.3)] transition-all active:scale-90 hover:scale-105 perspective-2000 preserve-3d ${showHelpMenu ? 'bg-gray-800 hover:bg-gray-900' : ''}`}
          >
            {showHelpMenu ? <X size={22} /> : <MessageCircle size={22} className="group-hover:rotate-y-180 transition-transform duration-700" />}
            <span className="font-black text-sm hidden sm:inline uppercase tracking-widest">{showHelpMenu ? 'Close Support' : 'Live Help Desk'}</span>
          </button>
        </div>

        {/* Footer System */}
        <footer className="bg-[#0a0a0a] text-white pt-24 pb-16 border-t border-white/5 relative z-10 perspective-2000 overflow-hidden">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-24">
              <div>
                <h4 className="text-sm font-black text-blue-500 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
                  <Box size={20} className="animate-float" /> Hub Profile
                </h4>
                <p className="text-gray-500 text-xs leading-relaxed mb-8 font-bold uppercase tracking-tight">
                  Premium student community providing direct verified Physics Wallah savings for the 2026 session. Managed by Jitesh Jana and the ambassador team.
                </p>
                <div className="flex items-center gap-4">
                  <a href={WHATSAPP_CHANNEL_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl hover:bg-[#25D366] transition-all border border-white/5 hover:scale-110" aria-label="WhatsApp Channel"><WhatsAppIcon size={18} /></a>
                  <a href={TELEGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl hover:bg-blue-600 transition-all border border-white/5 hover:scale-110" aria-label="Telegram Channel"><Send size={18} /></a>
                  <a href={INSTAGRAM_LINK} target="_blank" className="p-3 bg-white/5 rounded-2xl hover:bg-pink-600 transition-all border border-white/5 hover:scale-110" aria-label="Instagram Profile"><Instagram size={18} /></a>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-black text-white uppercase tracking-[0.3em] mb-8">Popular Codes</h4>
                <ul className="space-y-4 text-[11px] font-black uppercase tracking-widest text-gray-600">
                  <li><Link to="/pw-coupon-code" className="hover:text-blue-500 transition-colors">Universal Batch Code</Link></li>
                  <li><Link to="/pw-jee-coupon-code" className="hover:text-blue-500 transition-colors">JEE Mains & Advanced</Link></li>
                  <li><Link to="/pw-neet-coupon-code" className="hover:text-blue-500 transition-colors">NEET Medical Batches</Link></li>
                  <li><Link to="/pw-gate-coupon-code" className="hover:text-blue-500 transition-colors">GATE Engineering</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-sm font-black text-white uppercase tracking-[0.3em] mb-8">Resources</h4>
                <ul className="space-y-4 text-[11px] font-black uppercase tracking-widest text-gray-600">
                  <li><Link to="/blogs" className="hover:text-blue-500 transition-colors">Preparation Blog</Link></li>
                  <li><Link to="/vidyapeeth-vs-online" className="hover:text-blue-500 transition-colors">Vidyapeeth Centers</Link></li>
                  <li><Link to="/safety-and-transparency" className="hover:text-white transition-colors font-bold text-blue-500 underline decoration-blue-500/30">Safety & Transparency</Link></li>
                  <li><Link to="/legal-disclaimer" className="hover:text-blue-500 transition-colors flex items-center gap-2 underline decoration-gray-500/30">Legal Disclaimer <Scale size={12} /></Link></li>
                </ul>
              </div>

              <div className="bg-blue-600/5 p-8 rounded-[32px] border border-blue-600/10">
                <h4 className="text-sm font-black text-white uppercase tracking-[0.3em] mb-6">Ambassador Support</h4>
                <p className="text-gray-500 text-[10px] leading-relaxed mb-6 font-black uppercase tracking-wider">
                  Get priority help from certified student ambassadors via WhatsApp or Telegram.
                </p>
                <div className="flex flex-col gap-3">
                  <a 
                    href={WHATSAPP_CHANNEL_LINK} 
                    target="_blank" 
                    className="w-full inline-block bg-[#25D366] hover:bg-[#128C7E] text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] text-center shadow-lg transition-transform active:scale-95"
                  >
                    WHATSAPP CHANNEL
                  </a>
                  <a 
                    href={TELEGRAM_LINK} 
                    target="_blank" 
                    className="w-full inline-block bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] text-center shadow-lg transition-transform active:scale-95"
                  >
                    TELEGRAM CHAT
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t border-white/5 pt-16 text-center space-y-6">
              <div className="max-w-3xl mx-auto">
                <p className="text-[9px] text-gray-600 font-bold uppercase tracking-widest leading-relaxed mb-4">
                  <span className="text-gray-400 font-black">Quick Disclaimer:</span> PW Coupon Hub is an independent student resource and is not the official website of Physics Wallah Private Limited. All brand names and logos are trademarks of their respective owners. We may receive commissions for referrals at no extra cost to you.
                </p>
                <div className="flex justify-center gap-6">
                   <Link to="/legal-disclaimer" className="text-[10px] text-blue-500/50 hover:text-blue-400 font-black uppercase tracking-[0.2em] transition-colors">View Detailed Terms</Link>
                   <Link to="/safety-and-transparency" className="text-[10px] text-gray-700 hover:text-gray-500 font-black uppercase tracking-[0.2em] transition-colors">Security Report</Link>
                </div>
              </div>
              <p className="text-[10px] text-gray-800 font-black uppercase tracking-[0.5em]">
                © 2025 EDUOFFERS • THE ULTIMATE SAVINGS ENGINE • MANAGED BY JITESH JANA
              </p>
            </div>
          </div>
        </footer>
      </div>

      {/* Mobile Drawer */}
      <div className={`fixed inset-0 bg-black/80 backdrop-blur-md z-[60] transition-opacity duration-500 ${isMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`} onClick={() => setIsMenuOpen(false)} />
      <div className={`fixed top-0 left-0 h-full w-[320px] bg-[#0f0f0f] z-[70] shadow-[30px_0_60px_rgba(0,0,0,0.5)] border-r border-white/10 transition-transform duration-500 ease-out ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          <div className="p-8 border-b border-white/5 flex items-center justify-between">
            <span className="font-black text-2xl tracking-tighter text-white uppercase">EDU<span className="text-blue-500">OFFERS</span></span>
            <button onClick={() => setIsMenuOpen(false)} className="p-2 text-gray-500 hover:text-white transition-colors bg-white/5 rounded-xl"><X size={24} /></button>
          </div>
          <div className="flex-1 overflow-y-auto py-10 px-6">
            <nav className="space-y-3">
              {NAV_LINKS.filter(link => link.label !== 'Safety & Transparency').map((link) => (
                <Link key={link.path} to={link.path} onClick={() => setIsMenuOpen(false)} className={`flex items-center gap-4 px-5 py-4 rounded-[20px] text-sm font-black uppercase tracking-widest transition-all ${location.pathname === link.path ? 'bg-blue-600 text-white shadow-xl shadow-blue-600/20' : 'text-gray-500 hover:bg-white/5 hover:text-white'}`}>
                  {getIcon(link.label)}
                  {link.label}
                </Link>
              ))}
            </nav>
            {/* Dedicated Trust Link in Mobile Drawer Footer area */}
            <div className="mt-10 pt-10 border-t border-white/5">
              <Link 
                to="/safety-and-transparency" 
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-4 px-5 py-4 rounded-[20px] text-[10px] font-black uppercase tracking-widest text-blue-500 bg-blue-500/5 border border-blue-500/10 mb-3"
              >
                <ShieldAlert size={16} /> Safety & Transparency
              </Link>
              <Link 
                to="/legal-disclaimer" 
                onClick={() => setIsMenuOpen(false)}
                className="flex items-center gap-4 px-5 py-4 rounded-[20px] text-[10px] font-black uppercase tracking-widest text-gray-600 bg-white/5 border border-white/10"
              >
                <Scale size={16} /> Full Disclaimer
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
